/**
 * LPCEnemyPresets — Predefined LPC NPC archetypes for dungeon spawning.
 *
 * Each preset defines a body type, equipment loadout, combat stats, and display name.
 * At spawn time, `resolve(presetId, seed)` generates a deterministic appearance
 * via LPCRandomAppearance and merges the preset's equipment and combat config.
 */

import { generateRandomAppearance } from '../character/LPCRandomAppearance.js';
import SeededRandom from '../utils/SeededRandom.js';

// ─── Preset Definitions ─────────────────────────────────────────────────────

// Equipment pools: arrays mean "pick one randomly", strings are fixed.
// Optional slots use null entries to allow empty.

// Appearance overrides: same pool pattern as equipment.
// Keys match appearance fields (ears, facial, horns, tail, wings).
// Values are arrays (pick one, null = none) or fixed strings.

const PRESETS = {
  // ── Undead ──────────────────────────────────────────────────────────────
  'skeleton-warrior': {
    displayName: 'Skeleton Warrior',
    bodyType: 'skeleton-male',
    archetype: 'melee',
    equipment: {
      mainhand: ['iron-sword', 'iron-mace', 'war-axe', 'iron-spear', 'flail'],
      offhand:  [null, null, 'wooden-shield', 'iron-shield'],
      head:     [null, null, 'iron-helm', 'kettle-helm']
    },
    appearance: {
      horns: [null, null, null, null, 'head_horns_backwards', 'head_horns_curled']
    },
    combat: {
      maxHealth: 35,
      attackPower: 6,
      defense: 2,
      aggroRange: 8,
      deaggroRange: 14,
      attackRange: 1.8,
      attackCooldown: 1.1,
      lootTable: 'default'
    }
  },
  'skeleton-archer': {
    displayName: 'Skeleton Archer',
    bodyType: 'skeleton-male',
    archetype: 'archer',
    equipment: {
      mainhand: ['hunter-bow', 'great-bow', 'recurve-bow', 'crossbow']
    },
    appearance: {
      facial: [null, null, null, 'facial_eyepatch_left', 'facial_eyepatch_right']
    },
    combat: {
      maxHealth: 25,
      attackPower: 7,
      defense: 1,
      aggroRange: 10,
      deaggroRange: 16,
      attackRange: 1.8,
      rangedAttackRange: 10,
      projectileType: 'arrow',
      attackCooldown: 1.8,
      lootTable: 'default'
    }
  },
  'zombie': {
    displayName: 'Zombie',
    bodyType: 'zombie-male',
    archetype: 'melee',
    equipment: {
      body: [null, null, null, 'tunic', 'longsleeve-shirt'],
      legs: [null, null, null, 'cloth-leggings']
    },
    appearance: {
      hair: [null, null, null],  // zombies: mostly bald
      beard: [null, null, null]
    },
    combat: {
      maxHealth: 50,
      attackPower: 4,
      defense: 1,
      aggroRange: 6,
      deaggroRange: 10,
      attackRange: 1.8,
      attackCooldown: 1.6,
      lootTable: 'default'
    }
  },

  // ── Bandits ─────────────────────────────────────────────────────────────
  'bandit-melee': {
    displayName: 'Bandit',
    bodyType: null,
    bodyTypes: ['human-male', 'human-female'],
    archetype: 'melee',
    equipment: {
      mainhand: ['iron-sword', 'iron-dagger', 'iron-mace', 'scimitar', 'war-axe', 'iron-spear'],
      body:     ['leather-armor', 'tunic', 'tabard'],
      head:     [null, null, 'cloth-hood'],
      legs:     [null, 'leather-greaves', 'cloth-leggings', 'combat-shorts'],
      feet:     [null, 'leather-boots', 'sandals'],
      back:     [null, null, null, 'tattered-cloak', 'ragged-cloak']
    },
    appearance: {
      facial: [null, null, null, null, 'facial_eyepatch_left', 'facial_eyepatch_right',
               'facial_eyepatch_small_left', 'facial_mask_plain'],
      ears:   [null, null, null, null, 'head_ears_elven']
    },
    combat: {
      maxHealth: 40,
      attackPower: 7,
      defense: 3,
      aggroRange: 8,
      deaggroRange: 14,
      attackRange: 1.8,
      attackCooldown: 1.0,
      lootTable: 'elite'
    }
  },
  'bandit-archer': {
    displayName: 'Bandit Archer',
    bodyType: null,
    bodyTypes: ['human-male', 'human-female'],
    archetype: 'archer',
    equipment: {
      mainhand: ['hunter-bow', 'recurve-bow', 'great-bow', 'crossbow'],
      body:     ['leather-armor', 'tunic'],
      head:     ['cloth-hood', null, null],
      legs:     [null, 'leather-greaves', 'cloth-leggings'],
      feet:     [null, 'leather-boots'],
      back:     [null, null, 'woodsman-cloak', 'tattered-cloak']
    },
    appearance: {
      facial: [null, null, null, null, 'facial_eyepatch_left', 'facial_eyepatch_small_right'],
      ears:   [null, null, null, null, 'head_ears_elven']
    },
    combat: {
      maxHealth: 30,
      attackPower: 8,
      defense: 2,
      aggroRange: 10,
      deaggroRange: 16,
      attackRange: 1.8,
      rangedAttackRange: 10,
      projectileType: 'arrow',
      attackCooldown: 1.2,
      lootTable: 'elite'
    }
  },

  // ── Guards / Knights ────────────────────────────────────────────────────
  'guard': {
    displayName: 'Guard',
    bodyType: 'human-male',
    archetype: 'tank',
    equipment: {
      mainhand: ['iron-sword', 'iron-spear', 'iron-mace', 'halberd'],
      offhand:  [null, 'wooden-shield', 'iron-shield'],
      body:     ['leather-armor', 'chain-mail'],
      head:     ['iron-helm', 'kettle-helm'],
      legs:     [null, 'leather-greaves', 'chain-leggings'],
      feet:     ['leather-boots', 'iron-boots'],
      back:     [null, null, 'navy-cape']
    },
    combat: {
      maxHealth: 50,
      attackPower: 7,
      defense: 5,
      aggroRange: 8,
      deaggroRange: 14,
      attackRange: 1.8,
      attackCooldown: 1.0,
      lootTable: 'elite'
    }
  },
  'knight': {
    displayName: 'Knight',
    bodyType: 'human-male',
    archetype: 'tank',
    equipment: {
      mainhand:   ['steel-blade', 'golden-sword', 'katana', 'battle-axe', 'halberd'],
      offhand:    ['iron-shield', 'emerald-shield', 'crimson-shield', 'spartan-shield', null],
      body:       ['chain-mail', 'plate-armor', 'legion-armor'],
      head:       ['knight-helm', 'barbarian-helm', 'iron-helm'],
      shoulders:  [null, 'iron-pauldrons', 'steel-spaulders', 'legion-shoulders'],
      legs:       ['chain-leggings', 'plate-greaves'],
      feet:       ['iron-boots', 'steel-sabatons', 'plate-boots'],
      back:       [null, null, 'royal-cape', 'forest-cape', 'navy-cape']
    },
    combat: {
      maxHealth: 80,
      attackPower: 10,
      defense: 8,
      aggroRange: 8,
      deaggroRange: 14,
      attackRange: 1.8,
      attackCooldown: 1.2,
      lootTable: 'elite'
    }
  },

  // ── Mages ───────────────────────────────────────────────────────────────
  'dark-mage': {
    displayName: 'Dark Mage',
    bodyType: null,
    bodyTypes: ['human-male', 'human-female'],
    archetype: 'mage',
    equipment: {
      mainhand: ['fire-staff', 'crystal-staff', 'gnarled-staff', 'loop-staff', 'serpent-staff', 'magic-wand'],
      body:     ['mage-robe', 'tabard', 'longsleeve-shirt'],
      head:     ['wizard-hat', 'cloth-hood', null],
      back:     [null, 'mage-cloak', 'shadow-cloak', 'maroon-cloak'],
      feet:     [null, 'sandals', 'cloth-shoes']
    },
    appearance: {
      horns:  [null, null, null, null, 'head_horns_curled', 'head_horns_backwards'],
      ears:   [null, null, null, 'head_ears_elven', 'head_ears_long'],
      facial: [null, null, null, 'facial_monocle_left', 'facial_glasses_halfmoon']
    },
    combat: {
      maxHealth: 45,
      attackPower: 12,
      defense: 2,
      aggroRange: 10,
      deaggroRange: 16,
      attackRange: 1.8,
      rangedAttackRange: 9,
      projectileType: 'fireball',
      attackCooldown: 2.0,
      lootTable: 'elite'
    }
  },

  // ── Boss variants ───────────────────────────────────────────────────────
  'bandit-chief': {
    displayName: 'Bandit Chief',
    bodyType: 'human-muscular',
    archetype: 'boss',
    equipment: {
      mainhand:   ['katana', 'golden-sword', 'battle-axe', 'scythe', 'frost-blade'],
      body:       ['chain-mail', 'plate-armor', 'legion-armor'],
      head:       ['barbarian-helm', 'knight-helm'],
      shoulders:  ['leather-shoulders', 'iron-pauldrons', 'steel-spaulders'],
      legs:       ['chain-leggings', 'plate-greaves'],
      feet:       ['iron-boots', 'steel-sabatons'],
      back:       [null, 'shadow-cloak', 'maroon-cloak', 'royal-cape']
    },
    appearance: {
      facial: [null, null, 'facial_eyepatch2_left', 'facial_eyepatch2_right', 'facial_mask_plain'],
      ears:   [null, null, null, 'head_ears_elven']
    },
    combat: {
      maxHealth: 150,
      attackPower: 14,
      defense: 6,
      aggroRange: 12,
      deaggroRange: 18,
      attackRange: 2.2,
      attackCooldown: 1.3,
      lootTable: 'boss',
      isBoss: true
    }
  },
  'skeleton-lord': {
    displayName: 'Skeleton Lord',
    bodyType: 'skeleton-male',
    archetype: 'boss',
    equipment: {
      mainhand:  ['golden-sword', 'frost-blade', 'dragonspear', 'scythe', 'trident', 'halberd'],
      offhand:   ['iron-shield', 'crimson-shield', 'midnight-shield', 'tower-shield', null],
      head:      ['knight-helm', 'barbarian-helm'],
      shoulders: [null, 'golden-shoulders', 'steel-spaulders'],
      back:      [null, 'shadow-cloak', 'frost-cape', 'purple-cape']
    },
    appearance: {
      wings: [null, null, null, 'wings_bat', 'wings_lizard_bat', 'wings_lizard'],
      horns: [null, null, 'head_horns_backwards', 'head_horns_curled'],
      tail:  [null, null, null, 'tail_dragon', 'tail_lizard']
    },
    combat: {
      maxHealth: 130,
      attackPower: 13,
      defense: 7,
      aggroRange: 12,
      deaggroRange: 18,
      attackRange: 2.0,
      attackCooldown: 1.2,
      lootTable: 'boss',
      isBoss: true
    }
  },
  'arch-mage': {
    displayName: 'Arch Mage',
    bodyType: null,
    bodyTypes: ['human-male', 'human-female'],
    archetype: 'mage',
    equipment: {
      mainhand: ['crystal-staff', 'diamond-staff', 'serpent-staff'],
      body:     ['mage-robe'],
      head:     ['wizard-hat'],
      back:     ['mage-cloak', 'shadow-cloak', 'purple-cape', 'royal-cape'],
      feet:     [null, 'sandals', 'cloth-shoes'],
      shoulders: [null, 'epaulets', 'mantle']
    },
    appearance: {
      wings:  [null, null, 'wings_pixie', 'wings_pixie_transparent', 'wings_monarch',
               'wings_dragonfly', 'wings_dragonfly_transparent', 'wings_feathered', 'wings_lunar'],
      horns:  [null, null, null, 'head_horns_curled'],
      ears:   [null, null, 'head_ears_elven', 'head_ears_long'],
      facial: [null, 'facial_monocle_left_frame', 'facial_glasses_halfmoon', 'facial_glasses_round']
    },
    combat: {
      maxHealth: 120,
      attackPower: 16,
      defense: 3,
      aggroRange: 12,
      deaggroRange: 18,
      attackRange: 2.0,
      rangedAttackRange: 11,
      projectileType: 'fireball',
      attackCooldown: 2.2,
      lootTable: 'boss',
      isBoss: true
    }
  }
};

// ─── Public API ──────────────────────────────────────────────────────────────

/**
 * Check if a name is a known LPC preset (vs a legacy character name).
 * @param {string} name
 * @returns {boolean}
 */
export function isLPCPreset(name) {
  return name in PRESETS;
}

/**
 * Get all available preset IDs.
 * @returns {string[]}
 */
export function getPresetIds() {
  return Object.keys(PRESETS);
}

/**
 * Get preset metadata (for UI display).
 * @param {string} presetId
 * @returns {{ displayName: string, bodyType: string, equipment: Object, combat: Object } | null}
 */
export function getPreset(presetId) {
  return PRESETS[presetId] || null;
}

/**
 * Resolve a preset into a full spawn config with deterministic appearance.
 *
 * @param {string} presetId - Preset ID (e.g., 'skeleton-warrior')
 * @param {number} seed - Deterministic seed for appearance generation
 * @param {string} [skinColor] - Force skin color for group consistency (null = random)
 * @param {string} [hairColor] - Force hair color for group consistency (null = random)
 * @returns {{ lpcAppearance: Object, lpcEquipment: Object, combatConfig: Object, displayName: string }}
 */
export function resolvePreset(presetId, seed, skinColor, hairColor) {
  const preset = PRESETS[presetId];
  if (!preset) {
    throw new Error(`Unknown LPC preset: ${presetId}`);
  }

  // Pick body type — either fixed or random from options
  let bodyType = preset.bodyType;
  if (!bodyType && preset.bodyTypes) {
    // Use seed to deterministically pick from bodyTypes
    bodyType = preset.bodyTypes[Math.abs(seed) % preset.bodyTypes.length];
  }

  // Generate deterministic appearance with optional forced skin/hair
  const lpcAppearance = generateRandomAppearance({
    bodyType,
    seed,
    minimal: false,
    skinColor: skinColor || undefined,
    hairColor: hairColor || undefined
  });

  // Resolve pools — shared RNG for equipment + appearance overrides
  const poolRng = new SeededRandom(seed ^ 0x45515549);

  // Apply appearance overrides from preset (wings, horns, tail, facial, ears, etc.)
  if (preset.appearance) {
    for (const [key, value] of Object.entries(preset.appearance)) {
      if (Array.isArray(value)) {
        const pick = value[poolRng.nextInt(0, value.length)];
        lpcAppearance[key] = pick;  // null clears the slot
      } else {
        lpcAppearance[key] = value;
      }
    }
  }

  // Build equipment map — resolve pools (arrays) into single picks
  const lpcEquipment = {};
  for (const [slot, value] of Object.entries(preset.equipment)) {
    if (Array.isArray(value)) {
      const pick = value[poolRng.nextInt(0, value.length)];
      if (pick) lpcEquipment[slot] = pick;  // null = empty slot
    } else if (value) {
      lpcEquipment[slot] = value;
    }
  }

  // Build combat config with isHostile flag
  const combatConfig = {
    ...preset.combat,
    isHostile: true
  };

  return {
    lpcAppearance,
    lpcEquipment,
    combatConfig,
    displayName: preset.displayName,
    archetype: preset.archetype || 'melee'
  };
}
